import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { createUser, getRoles } from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import { timezones } from "../../../Static/Data/_Common/Timezones";

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role: "",
  orgRole: "",
  timezone: "Asia/Kolkata",
};

const CreateUserDialog = ({ open, onClose, onSuccess }) => {
  const { openNotification } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setRolesLoading(true);
      getRoles()
        .then((res) => {
          if (res?.responseStatus?.code === CODE.SUCCESS) {
            setRoles(res.response?.data || []);
          }
        })
        .finally(() => setRolesLoading(false));
    }
  }, [open]);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await createUser({
        username: form.email,
        password: form.password,
        name: form.name,
        email: form.email,
        role: form.role,
        orgRoleCode: form.orgRole,
        fleetID: 0,
        timezone: form.timezone,
      });
      if (res?.responseStatus?.code === CODE.URL_CREATED) {
        if (res.orgRoleWarning) {
          openNotification({ text: "User created but org role assignment failed", icon: "toastInfo" });
        } else {
          openNotification({ text: "User created successfully", icon: "toast-success" });
        }
        onSuccess();
      } else {
        openNotification({
          text: res?.responseStatus?.msg || "Failed to create user. Please try again.",
          icon: "toast-error",
        });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.responseStatus?.msg ||
        err?.response?.data?.msg ||
        "Failed to create user. Please try again.";
      openNotification({ text: msg, icon: "toast-error" });
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled =
    saving ||
    !form.name.trim() ||
    !form.email.trim() ||
    !form.password.trim() ||
    !form.role ||
    !form.orgRole ||
    !form.timezone;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 20, fontWeight: 700, pb: 1 }}>
        Create New User
      </DialogTitle>
      <DialogContent sx={{ pt: "16px !important" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={handleChange("name")}
            fullWidth
            required
            inputProps={{ style: { fontSize: 15 } }}
            InputLabelProps={{ style: { fontSize: 15 } }}
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={handleChange("email")}
            fullWidth
            required
            type="email"
            inputProps={{ style: { fontSize: 15 } }}
            InputLabelProps={{ style: { fontSize: 15 } }}
          />
          <TextField
            label="Password"
            value={form.password}
            onChange={handleChange("password")}
            fullWidth
            required
            type="password"
            inputProps={{ style: { fontSize: 15 } }}
            InputLabelProps={{ style: { fontSize: 15 } }}
          />
          <FormControl fullWidth required>
            <InputLabel sx={{ fontSize: 15 }}>Role</InputLabel>
            <Select
              value={form.role}
              onChange={handleChange("role")}
              label="Role"
              sx={{ fontSize: 15 }}
            >
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel sx={{ fontSize: 15 }}>Org Role</InputLabel>
            <Select
              value={form.orgRole}
              onChange={handleChange("orgRole")}
              label="Org Role"
              disabled={rolesLoading}
              sx={{ fontSize: 15 }}
            >
              {roles.map((r) => (
                <MenuItem key={r.id} value={r.roleCode}>
                  {r.roleName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel sx={{ fontSize: 15 }}>Timezone</InputLabel>
            <Select
              value={form.timezone}
              onChange={handleChange("timezone")}
              label="Timezone"
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              sx={{ fontSize: 15 }}
            >
              {timezones.map((tz, index) => (
                <MenuItem key={index} value={tz.name} sx={{ fontSize: 14 }}>
                  ({tz.offset}) {tz.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} sx={{ fontSize: 14 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
          sx={{ fontSize: 14, minWidth: 90 }}
        >
          {saving ? <CircularProgress size={18} /> : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
